<template lang="pug">
  .make-an-entry-nutrition
    section.section.pb-1.pt-2.mt-2
      MakeAnEntryVisibility(text="Always Private")
      .make-an-entry-content
        WtTitle(h4 bold style="margin-bottom:5px") Nutrition
        WtLabel(
          style="color:#5A5C60"
          text="Indicate how you would rate your food choices for today"
        )
        .slider-main-container.p-6
          BField
            BSlider.slider-element.is-primary(
              :min=1
              :max=10
              v-model="nutrition"
              :tooltip="false"
              :ticks="false"
            )
              BSliderTick(v-for="tickData in ticksData" :value="tickData.label" :key="tickData.label")
                .tick-number-container(:class="['tick-container-' + nutrition]")
                  WtSvgIcon(icon="slider_number_selector/number_selector" width="100px" height="50px" v-if="nutrition===tickData.label")
                  .tick-number(:class="nutrition===tickData.label ? 'active-tick-number' : ''") {{tickData.label}}

          p.tick-description(
            v-for="tickData in ticksData"
            v-if="nutrition === tickData.label"
            :class="['nutrition-description-' + nutrition]"
          )  {{ tickData.description }}
        MakeAnEntryTip(:tip-category="'nutrition'")
    .level.is-mobile.program-settings-form-controls
      .level-left
      .level-right
        .level-item
          WtButton(
            is-text
            color="white"
            style="text-decoration:none"
            size="medium"
            @click.native="reset"
            :disabled="disableReset"
          ) Reset
        .level-item
          WtButton(@click.native="saveEntry" :loading="savingNutrition" :disabled="disableSave") SAVE
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import MakeAnEntryFooter from "@@/Members/MakeAnEntry/MakeAnEntryFooter";
import MakeAnEntryVisibility from "@@/Members/MakeAnEntry/MakeAnEntryVisibility";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtLabel from "@@/WtLabel/WtLabel";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";
import MakeAnEntryTip from "@@/Members/MakeAnEntry/MakeAnEntryTip";

export default {
  name: "MakeAnEntryNutrition",

  components: {
    WtLabel,
    WtTitle,
    WtButton,
    MakeAnEntryFooter,
    MakeAnEntryVisibility,
    WtSvgIcon,
    MakeAnEntryTip
  },

  mixins: [dateUtilityMixin],

  props: {
    uniqueKey: {
      type: String,
      default: null
    },

    fetchAction: {
      type: Function,
      default: () => {},
      required: true
    },

    // "journal" or "discussion"
    type: {
      type: String,
      default: "journal"
    },

    entryItem: {
      type: Object,
      default: () => {},
      required: true
    },

    challengeId: {
      type: Number,
      default: null
    },

    entryDate: {
      type: String,
      default: ""
    },

    formData: {
      type: FormData,
      required: true,
      validator: value => {
        return value instanceof FormData;
      }
    },

    saveEntryWrapper: {
      type: Function,
      required: true
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    disableReset: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      nutrition: 1,
      savingNutrition: false,
      ticksData: [
        {
          label: 1,
          description:
            "Excess portions of fast food, processed food or sugary foods."
        },
        {
          label: 2,
          description:
            "Immoderate portions of fast food, processed food or sugary foods."
        },
        {
          label: 3,
          description:
            "A fair amount of sugar, processed foods, and very little fruit, vegetables, whole grains, or protein. "
        },
        {
          label: 4,
          description:
            "A fair amount of sugar and processed foods. Little fruit, vegetables, whole grains, protein."
        },
        {
          label: 5,
          description:
            "Some processed foods and sugar - portions of fruit and vegetables."
        },
        {
          label: 6,
          description:
            "Some fruit, vegetables, whole grains, protein. Some processed foods and sugar."
        },
        {
          label: 7,
          description:
            "A decent (but less than ideal) amount of fruit, vegetables, whole grains, protein. Some processed foods and sugar."
        },
        {
          label: 8,
          description:
            "Fruit, vegetables, whole grains, some protein, some processed foods or sugar."
        },
        {
          label: 9,
          description:
            "Fruits, vegetables, whole grains, some protein, very minimal processed foods or sugar."
        },
        {
          label: 10,
          description:
            "Fruits, vegetables, whole grains, adequate protein intake for your body/requirements, no processed foods, proper-sized portions."
        }
      ]
    };
  },

  computed: {
    ...mapGetters("program", ["coreProgramMeta"]),

    setFormData() {
      if (!this._.isEmpty(this.entryItem)) {
        // either it's changed or it might have been changed and changed back to what it was
        return (
          this.nutrition !== this.currentMetricValue ||
          this.formData.has("nutrition")
        );
      }
      return true;
    },

    currentMetricValue() {
      let metric = this._.find(this.entryItem.wellnessMetrics, {
        type: "nutrition"
      });
      return this._.get(metric, "value", 1);
    }
  },

  watch: {
    nutrition() {
      if (this.setFormData) {
        this.formData.set("nutrition", this.nutrition);
        this.$emit("formDataUpdated", true);
      }
    }
  },

  mounted() {
    this.nutrition = this.currentMetricValue;
  },

  methods: {
    ...mapActions("members", ["updateJournal", "storeJournal"]),

    saveEntry() {
      this.savingNutrition = true;
      this.saveEntryWrapper(this.storeJournal, this.updateJournal);
    },

    reset() {
      const item = "nutrition";
      if (this.formData.has(item)) {
        this.formData.delete(item);
      }
      let metric = this._.find(this.entryItem.wellnessMetrics, {
        type: item
      });
      this.nutrition = this._.get(metric, "value", 1);
      this.$nextTick(() => {
        this.$emit("formReset", true);
      });
    }
  }
};
</script>

<style lang="sass">
.make-an-entry-nutrition
  .wellness-visibility
    position: absolute
    right: 1rem
    top: .5rem
    display: flex
    align-items: center

  .b-slider .b-slider-thumb
    height: 1.4rem
    width: 1.4rem
    border-radius: 50%

  .b-slider .b-slider-tick
    background-color: transparent

  .slider-main-container
    overflow: hidden
    padding: 40px 0
    display: flex
    align-items: center
    justify-content: center
    flex-direction: column
    width: 650px
    max-width: 100%

  .slider-element
    position: relative
    max-width: 88%

  .tick-number
    text-align: center
    width: 40px
    margin-top: 15px
    display: flex
    align-items: center
    justify-content: center
    font-size: 15px

  .tick-description
    background-color: $teal
    padding: 2rem
    color: white
    text-align: center
    border-radius: 12px
    font-size: 16px
    height: 88px
    display: flex
    align-items: center
    justify-content: center
    margin-top: 32px
    width: 110%
    @media screen and (max-width: 540px)
      font-size: 14px
      margin-top: 32px

  .active-tick-number
    padding: 0 15px 15px 15px
    height: 40px
    position: absolute
    border-radius: 20px 20px 0 0
    color: white

  .tick-number-container
    display: flex
    align-items: center
    justify-content: center
    margin-top: 10px

  .field
    width: 100%
    display: flex
    align-items: center
    justify-content: center
    @media screen and (max-width: 768px)
      width: 90%
    @media screen and (max-width: 675px)
      width: 80%
    @media screen and (max-width: 540px)
      width: 75%
.program-settings-form-controls
  margin-top: 20px
.b-slider.is-primary .b-slider-fill
  background: #1F9E9E !important
</style>
